import { SyntheticEvent, useState, useCallback, useEffect } from 'react';
import { Autocomplete, AutocompleteProps, TextFieldProps, SxProps } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';

import TextFieldSearchBar from '../TextFieldSearchBar';

import ListboxComponent from './ListboxComponent';
import OptionComponent from './OptionComponent';

export type OptionType = {
    text: string;
    topic?: string;
};

interface AutocompleteSearchBarProps
    extends Omit<AutocompleteProps<string, false, boolean, true>, 'renderInput' | 'options'> {
    onChange: (event: SyntheticEvent<Element, Event>, value?: string | null) => void;
    inputRef?: React.RefObject<HTMLInputElement>;
    placeholder: string;
    sx?: SxProps;
    textFieldProps?: TextFieldProps;
    options: OptionType[];
}

function AutocompleteSearchBar({
    onChange: onChangeCallback,
    inputRef = undefined,
    options,
    placeholder,
    sx = {},
    value,
    textFieldProps = {},
    ...restProps
}: AutocompleteSearchBarProps) {
    const [internalValue, setInternalValue] = useState<string | null>(null);
    const [internalInputValue, setInternalInputValue] = useState<string>('');

    const shuffleMatches = (matches: OptionType[]) => {
        const shuffledMatches = [...matches];
        // Shuffle the filtered array to avoid showing the same options if more than 5 options are returned
        for (let i = matches.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledMatches[i], shuffledMatches[j]] = [shuffledMatches[j], shuffledMatches[i]];
        }

        return shuffledMatches.slice(0, 5).map((option) => option.text);
    };

    const filterOptions = useCallback((autoCompleteOptions: OptionType[], { inputValue }: { inputValue: string }) => {
        if (inputValue.length < 3) {
            return [];
        }
        const questionMatches = [...autoCompleteOptions].filter((option) =>
            option.text.toLowerCase().includes(inputValue.toLowerCase())
        );

        if (questionMatches.length > 0) {
            return shuffleMatches(questionMatches);
        }
        const topicMatches = [...autoCompleteOptions].filter((option) =>
            option.topic?.toLowerCase().includes(inputValue.toLowerCase())
        );
        return shuffleMatches(topicMatches);
    }, []);

    const handleChange = (event: SyntheticEvent<Element, Event>, newValue: string | null) => {
        setInternalValue(newValue);
        onChangeCallback(event, newValue);
    };

    const handleInputChange = (event: SyntheticEvent<Element, Event>, newInputValue: string) => {
        setInternalInputValue(newInputValue);
        onChangeCallback(event, newInputValue);
    };

    useEffect(() => {
        if (value === null || value === undefined) {
            return;
        }

        if (value !== internalValue) {
            setInternalInputValue(value);
        }
    }, [value, internalValue]);

    return (
        <Autocomplete
            freeSolo
            blurOnSelect
            value={internalValue}
            onChange={handleChange}
            inputValue={internalInputValue}
            onInputChange={handleInputChange}
            disableClearable={internalInputValue.length === 0}
            clearIcon={<CloseRounded sx={{ color: 'cement.500' }} />}
            // @ts-expect-error Changed Options type
            options={options}
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(option, inputValue) => option === inputValue}
            // @ts-expect-error Changed Options type
            filterOptions={filterOptions}
            fullWidth
            ListboxComponent={ListboxComponent}
            slotProps={{
                popper: {
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 4],
                            },
                        },
                    ],
                },
            }}
            renderInput={(params) => (
                <TextFieldSearchBar
                    {...textFieldProps}
                    {...{
                        ...params,
                        InputProps: {
                            ...params.InputProps,
                            startAdornment: textFieldProps.InputProps?.startAdornment ?? null,
                        },
                    }}
                    inputRef={inputRef}
                    placeholder={placeholder}
                    sx={{
                        ...sx,
                    }}
                />
            )}
            renderOption={(props, option, state) => {
                return <OptionComponent option={option} state={state} {...props} />;
            }}
            {...restProps}
        />
    );
}

export default AutocompleteSearchBar;
